import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import {
  normalizeItem,
  getHazardNonconformities as adminGetHazardNonconformities,
} from './inspectionAdminService';
import i18n from "../i18n";

// ─────────────────────────────────────────────────────────────────────────────
// sahaInspectionService.js  —  Inspector/Saha form filling service
//
// FLAG: USE_MOCK_DATA = true → localStorage mock  |  false → gerçek API
//
// API endpoint mapping:
//   getScopes()            → GET  /api/v1/inspection/scopes?languageId=1
//   getTypes(scopeId)      → GET  /api/v1/inspection/types?parentId={scopeId}&languageId=1
//   getFormTree(typeId)    → GET  /api/v1/inspection/generate?typeId={typeId}&languageId=1
//   getNcTree(questionId)  → GET  /api/v1/inspection/nonconformity-tree?questionId={questionId}&languageId=1
//   getTargets(entity)     → entity bazlı: stand/salon/fuar_alani/fuar endpoint'leri
//   checkOrSave / saveFilledForm → POST /api/v1/inspection/form/checkOrSave
//   getAnswers(formId)     → GET  /api/v1/inspection/form/{formId}/latest-contents
//   saveAnswer(...)        → PUT  /api/v1/inspection/form/content
//   getAllFilledForms(...)  → GET  /api/v1/inspection/forms?page=&limit=&fuarId=&inspectionLevelId=
//   updateFilledForm(id,..)→ POST /api/v1/inspection/form/{id}/partial
// ─────────────────────────────────────────────────────────────────────────────

export const USE_MOCK_DATA = false; // FLAG: true → mock  |  false → gerçek API ✅

const INSPECTION_API = `${JAVA_BACKEND_API_URL}/inspection`;

// ── 1. Scope'lar ─────────────────────────────────────────────────────────────
// API: GET /api/v1/inspection/scopes
export const MOCK_SCOPES = [
  { inspection_scope_id: 1, inspection_scope_name: "Stand Denetimi",      target_entity: "stand",      inspection_scope_description: "Stand bazlı denetimler" },
  { inspection_scope_id: 2, inspection_scope_name: "Salon Denetimi",      target_entity: "salon",      inspection_scope_description: "Salon bazlı denetimler" },
  { inspection_scope_id: 3, inspection_scope_name: "Fuar Alanı Denetimi", target_entity: "fuar_alani", inspection_scope_description: "Fuar alanı bazlı denetimler" },
  { inspection_scope_id: 4, inspection_scope_name: "Fuar Denetimi",       target_entity: "fuar",       inspection_scope_description: "Fuarın tamamına yönelik denetimler" },
];

// ── 2. Denetim Tipleri ────────────────────────────────────────────────────────
// API: GET /api/v1/inspection/types?parentId={scopeId}
export const MOCK_TYPES = {
  1: [ // Stand scope
    { inspection_type_id: 1, inspection_scope_id: 1, inspection_type_name: "İSG Denetim",          inspection_type_short: "ISG", main_question: "Bu stand İSG gerekliliklerine uygun mu?",       main_question_quest_type: 1 },
    { inspection_type_id: 2, inspection_scope_id: 1, inspection_type_name: "Elektrik Güvenlik",    inspection_type_short: "ELK", main_question: "Elektrik tesisatı yönetmeliklere uygun mu?",     main_question_quest_type: 1 },
    { inspection_type_id: 3, inspection_scope_id: 1, inspection_type_name: "Acil Durum",           inspection_type_short: "ACD", main_question: "Acil durum prosedürleri eksiksiz mi?",           main_question_quest_type: 1 },
    { inspection_type_id: 4, inspection_scope_id: 1, inspection_type_name: "Tadilat İzni",         inspection_type_short: "TAD", main_question: "Tadilat çalışmaları izne uygun mu?",              main_question_quest_type: 1 },
    { inspection_type_id: 5, inspection_scope_id: 1, inspection_type_name: "Better Stand Kontrol", inspection_type_short: "BTS", main_question: "Better stand standartları karşılanıyor mu?",     main_question_quest_type: 1 },
  ],
  2: [ // Salon scope
    { inspection_type_id: 6, inspection_scope_id: 2, inspection_type_name: "Salon Genel Denetim", inspection_type_short: "SGD", main_question: "Salon genel güvenlik standartlarını karşılıyor mu?", main_question_quest_type: 1 },
    { inspection_type_id: 7, inspection_scope_id: 2, inspection_type_name: "Yangın Güvenliği",    inspection_type_short: "YGN", main_question: "Salon yangın güvenliği önlemleri yeterli mi?",        main_question_quest_type: 1 },
  ],
  3: [ // Fuar Alanı scope
    { inspection_type_id: 8, inspection_scope_id: 3, inspection_type_name: "Alan Genel Denetim", inspection_type_short: "AGD", main_question: "Fuar alanı genel güvenlik standartlarını karşılıyor mu?", main_question_quest_type: 1 },
    { inspection_type_id: 9, inspection_scope_id: 3, inspection_type_name: "Altyapı Kontrolü",   inspection_type_short: "ALT", main_question: "Altyapı ve tesisat standartlara uygun mu?",                 main_question_quest_type: 1 },
  ],
  4: [ // Fuar scope
    { inspection_type_id: 10, inspection_scope_id: 4, inspection_type_name: "Fuar Genel Denetim", inspection_type_short: "FGD", main_question: "Fuar genel standartlara uygun mu?", main_question_quest_type: 1 },
  ],
};

// ── 3. Form Ağacı ─────────────────────────────────────────────────────────────
// API: GET /api/v1/inspection/generate?typeId={typeId}
// Şimdilik sadece 1 ve 2 nolu tipler için detaylı ağaç var; diğerleri placeholder
export const MOCK_FORM_TREES = {
  1: {
    typeId: 1, typeName: "İSG Denetim",
    mainQuestion: "Bu stand İSG gerekliliklerine uygun mu?",
    mainQuestionQuestType: 1,
    categories: [
      {
        categoryId: 10, categoryName: "KKD (Kişisel Koruyucu Donanım)",
        questions: [
          { questionId: 42, questionName: "Baret",                          questType: 1, photoRequired: true  },
          { questionId: 43, questionName: "Yelek (Reflektif)",              questType: 1, photoRequired: false },
          { questionId: 44, questionName: "Eldiven",                        questType: 1, photoRequired: false },
          { questionId: 45, questionName: "Güvenlik Ayakkabısı",           questType: 1, photoRequired: true  },
        ],
      },
      {
        categoryId: 11, categoryName: "Yangın Güvenliği",
        questions: [
          { questionId: 50, questionName: "Yangın Tüpü (Mevcut & Dolu)",   questType: 1, photoRequired: true  },
          { questionId: 51, questionName: "Yangın Tüpü (Etiketi Geçerli)", questType: 1, photoRequired: false },
          { questionId: 52, questionName: "Yangın Çıkışı (Açık & İşaretli)", questType: 1, photoRequired: true },
        ],
      },
      {
        categoryId: 12, categoryName: "Elektrik Güvenliği",
        questions: [
          { questionId: 60, questionName: "Kablo Düzeni (Takılma Riski Yok)", questType: 1, photoRequired: false },
          { questionId: 61, questionName: "Topraklama Bağlantısı",            questType: 1, photoRequired: true  },
          { questionId: 62, questionName: "Sigortalar (Uygun Amper)",          questType: 1, photoRequired: false },
        ],
      },
      {
        categoryId: 13, categoryName: "Genel Düzen & Temizlik",
        questions: [
          { questionId: 70, questionName: "Zemin (Engel Yok, Temiz)",    questType: 1, photoRequired: false },
          { questionId: 71, questionName: "Stant Arkası Düzen",          questType: 1, photoRequired: false },
          { questionId: 72, questionName: "Malzeme İstifleme (Güvenli)", questType: 1, photoRequired: true  },
        ],
      },
    ],
  },
  2: {
    typeId: 2, typeName: "Elektrik Güvenlik",
    mainQuestion: "Elektrik tesisatı yönetmeliklere uygun mu?",
    mainQuestionQuestType: 1,
    categories: [
      {
        categoryId: 20, categoryName: "Tesisat Kontrolü",
        questions: [
          { questionId: 80, questionName: "Kablo İzolasyonu",                questType: 1, photoRequired: true  },
          { questionId: 81, questionName: "Bağlantı Noktaları",              questType: 1, photoRequired: false },
          { questionId: 82, questionName: "Pano Erişimi (Engelsiz)",         questType: 1, photoRequired: false },
          { questionId: 83, questionName: "Topraklama Bağlantısı",           questType: 1, photoRequired: true  },
        ],
      },
      {
        categoryId: 21, categoryName: "Ekipman Kontrolü",
        questions: [
          { questionId: 84, questionName: "Uzatma Kablosu (CE Belgeli)",     questType: 1, photoRequired: false },
          { questionId: 85, questionName: "Yüksek Güçlü Cihaz İzni",        questType: 1, photoRequired: true  },
          { questionId: 86, questionName: "Fiş/Priz Durumu",                 questType: 1, photoRequired: false },
        ],
      },
      {
        categoryId: 22, categoryName: "Aydınlatma",
        questions: [
          { questionId: 87, questionName: "Acil Aydınlatma (Çalışıyor)",    questType: 1, photoRequired: false },
          { questionId: 88, questionName: "Yeterli Aydınlatma Seviyesi",    questType: 1, photoRequired: false },
        ],
      },
    ],
  },
  3: {
    typeId: 3, typeName: "Acil Durum",
    mainQuestion: "Acil durum prosedürleri eksiksiz mi?",
    mainQuestionQuestType: 1,
    categories: [
      {
        categoryId: 30, categoryName: "Acil Durum Ekipmanları",
        questions: [
          { questionId: 90, questionName: "İlk Yardım Çantası",             questType: 1, photoRequired: true  },
          { questionId: 91, questionName: "Toplanma Noktası İşareti",       questType: 1, photoRequired: false },
          { questionId: 92, questionName: "Tahliye Planı (Görünür Yerde)",  questType: 1, photoRequired: true  },
        ],
      },
    ],
  },
  4: {
    typeId: 4, typeName: "Tadilat İzni",
    mainQuestion: "Tadilat çalışmaları izne uygun mu?",
    mainQuestionQuestType: 1,
    categories: [
      {
        categoryId: 40, categoryName: "İzin Belgeleri",
        questions: [
          { questionId: 100, questionName: "Tadilat İzin Belgesi",         questType: 1, photoRequired: true  },
          { questionId: 101, questionName: "Statik Proje Onayı",           questType: 1, photoRequired: false },
          { questionId: 102, questionName: "Elektrik Proje Onayı",         questType: 1, photoRequired: false },
        ],
      },
      {
        categoryId: 41, categoryName: "Saha Güvenliği",
        questions: [
          { questionId: 103, questionName: "Bariyerler (Yeterli & Güvenli)", questType: 1, photoRequired: true  },
          { questionId: 104, questionName: "İkaz Levhaları",                 questType: 1, photoRequired: false },
        ],
      },
    ],
  },
  5: {
    typeId: 5, typeName: "Better Stand Kontrol",
    mainQuestion: "Better stand standartları karşılanıyor mu?",
    mainQuestionQuestType: 1,
    categories: [
      {
        categoryId: 50, categoryName: "Montaj Kalitesi",
        questions: [
          { questionId: 110, questionName: "Profil Bağlantıları (Sağlam)", questType: 1, photoRequired: true  },
          { questionId: 111, questionName: "Köşe Birleşimleri",            questType: 1, photoRequired: false },
          { questionId: 112, questionName: "Zemin Sabitleme",              questType: 1, photoRequired: true  },
        ],
      },
      {
        categoryId: 51, categoryName: "Estetik & Marka",
        questions: [
          { questionId: 113, questionName: "Logo Yerleşimi",               questType: 1, photoRequired: false },
          { questionId: 114, questionName: "Renk Uyumu",                   questType: 1, photoRequired: false },
          { questionId: 115, questionName: "Hasarsız Yüzeyler",            questType: 1, photoRequired: true  },
        ],
      },
    ],
  },
};

// ── 4. Uygunsuzluk Ağacı ──────────────────────────────────────────────────────
// API: GET /api/v1/inspection/nonconformity-tree?questionId={questionId}
export const MOCK_NC_TREES = {
  42: {
    questionId: 42, questionName: "Baret",
    hazards: [
      {
        hazardId: 88, hazardName: "Baret takılmamış",
        risks: [
          {
            riskId: 120, riskName: "Düşen cisim — kafa yaralanması",
            nonconformities: [
              { nonconformityId: 200, nonconformityName: "Baret eksikliği - Seviye 1", olasilik: 3, frekans: 4, siddet: 2, riskDegeri: 24, oneri: "Derhal baret temin edilmeli ve kullanım zorunlu kılınmalı" },
              { nonconformityId: 201, nonconformityName: "Baret eksikliği - Seviye 2 (Kritik)", olasilik: 4, frekans: 4, siddet: 3, riskDegeri: 48, oneri: "Çalışma durdurulmalı, baret olmadan devam edilemez" },
            ],
          },
        ],
      },
      {
        hazardId: 89, hazardName: "Baret standarda uygun değil",
        risks: [
          {
            riskId: 121, riskName: "Yetersiz koruma — kırılma riski",
            nonconformities: [
              { nonconformityId: 202, nonconformityName: "Standart dışı baret", olasilik: 2, frekans: 3, siddet: 3, riskDegeri: 18, oneri: "CE sertifikalı baret temin edilmeli" },
            ],
          },
        ],
      },
    ],
  },
  50: {
    questionId: 50, questionName: "Yangın Tüpü (Mevcut & Dolu)",
    hazards: [
      {
        hazardId: 90, hazardName: "Yangın tüpü yok veya boş",
        risks: [
          {
            riskId: 130, riskName: "Yangın müdahale imkânı yok",
            nonconformities: [
              { nonconformityId: 210, nonconformityName: "Yangın tüpü eksik", olasilik: 2, frekans: 2, siddet: 5, riskDegeri: 20, oneri: "Derhal yangın tüpü temin edilmeli" },
            ],
          },
        ],
      },
    ],
  },
  61: {
    questionId: 61, questionName: "Topraklama Bağlantısı",
    hazards: [
      {
        hazardId: 91, hazardName: "Topraklama yok veya hatalı",
        risks: [
          {
            riskId: 131, riskName: "Elektrik çarpması riski",
            nonconformities: [
              { nonconformityId: 211, nonconformityName: "Topraklama eksikliği", olasilik: 3, frekans: 3, siddet: 5, riskDegeri: 45, oneri: "Yetkili elektrikçi tarafından topraklama yapılmalı" },
            ],
          },
        ],
      },
    ],
  },
};

// Soruya ait NC ağacı yoksa genel bir placeholder döner
const DEFAULT_NC_TREE = (questionId, questionName) => ({
  questionId,
  questionName,
  hazards: [
    {
      hazardId: 999, hazardName: "Genel Tehlike",
      risks: [
        {
          riskId: 999, riskName: "Genel Risk",
          nonconformities: [
            { nonconformityId: 999, nonconformityName: "Uygunsuzluk Tespit Edildi", olasilik: 2, frekans: 2, siddet: 2, riskDegeri: 8, oneri: "Düzeltici faaliyet başlatılmalı" },
          ],
        },
      ],
    },
  ],
});

// ── 5. Hedef Varlıklar ────────────────────────────────────────────────────────
// API: targetEntity string'e göre farklı endpoint çağrılır
export const MOCK_TARGETS = {
  stand: [
    { id: 42, name: "A-34", firma: "Akdeniz A.Ş.",    salon: "1. Salon" },
    { id: 43, name: "B-12", firma: "Karadeniz Ltd.",  salon: "1. Salon" },
    { id: 44, name: "C-07", firma: "Ege Tic.",         salon: "2. Salon" },
    { id: 45, name: "D-21", firma: "Marmara San.",     salon: "2. Salon" },
    { id: 46, name: "E-03", firma: "Tüm Stand A.Ş.", salon: "3. Salon" },
    { id: 47, name: "F-15", firma: "İstanbul Fuar",   salon: "1. Salon" },
    { id: 48, name: "G-08", firma: "Ankara Tic.",      salon: "3. Salon" },
    { id: 49, name: "H-22", firma: "Boğaz A.Ş.",      salon: "2. Salon" },
  ],
  salon: [
    { id: 10, name: "1. Salon", firma: null },
    { id: 11, name: "2. Salon", firma: null },
    { id: 12, name: "3. Salon", firma: null },
  ],
  fuar_alani: [
    { id: 1, name: "Ana Fuar Alanı (İstanbul)", firma: null },
  ],
  fuar: [
    { id: 3, name: "FDS Fuar 2026 (Aktif)", firma: null },
  ],
};

// ── 6. Doldurulmuş Form Kayıtları (MongoDB mock) ──────────────────────────────
// API: GET /api/v1/inspection/forms?...
const FILLED_FORMS_KEY = "fds_saha_filled_forms";

const SEED_FILLED_FORMS = [
  { id: "saha001", inspectionLevelId: 1, inspectionTypeId: 1, inspectionScopeId: 1, scopeTargetId: 42, firmaId: 1,  fuarId: 3, userId: 17, isDone: true,  notes: "",  parentFormId: null,    createdAt: "2026-04-08T08:00:00Z", completedAt: "2026-04-08T09:30:00Z" },
  { id: "saha002", inspectionLevelId: 2, inspectionTypeId: 1, inspectionScopeId: 1, scopeTargetId: 42, firmaId: 1,  fuarId: 3, userId: 17, isDone: false, notes: "",  parentFormId: "saha001", createdAt: "2026-04-08T09:45:00Z", completedAt: null },
  { id: "saha003", inspectionLevelId: 1, inspectionTypeId: 2, inspectionScopeId: 1, scopeTargetId: 43, firmaId: 2,  fuarId: 3, userId: 18, isDone: true,  notes: "",  parentFormId: null,    createdAt: "2026-04-08T10:00:00Z", completedAt: "2026-04-08T11:00:00Z" },
  { id: "saha004", inspectionLevelId: 1, inspectionTypeId: 1, inspectionScopeId: 1, scopeTargetId: 44, firmaId: 3,  fuarId: 3, userId: 17, isDone: false, notes: "",  parentFormId: null,    createdAt: "2026-04-09T08:30:00Z", completedAt: null },
  { id: "saha005", inspectionLevelId: 3, inspectionTypeId: 1, inspectionScopeId: 1, scopeTargetId: 42, firmaId: 1,  fuarId: 3, userId: 19, isDone: true,  notes: "",  parentFormId: "saha001", createdAt: "2026-04-09T10:00:00Z", completedAt: "2026-04-09T11:30:00Z" },
  { id: "saha006", inspectionLevelId: 2, inspectionTypeId: 2, inspectionScopeId: 1, scopeTargetId: 43, firmaId: 2,  fuarId: 3, userId: 18, isDone: false, notes: "",  parentFormId: "saha003", createdAt: "2026-04-09T11:15:00Z", completedAt: null },
  { id: "saha007", inspectionLevelId: 1, inspectionTypeId: 3, inspectionScopeId: 1, scopeTargetId: 45, firmaId: 4,  fuarId: 3, userId: 20, isDone: true,  notes: "",  parentFormId: null,    createdAt: "2026-04-09T14:00:00Z", completedAt: "2026-04-09T15:30:00Z" },
  { id: "saha008", inspectionLevelId: 1, inspectionTypeId: 6, inspectionScopeId: 2, scopeTargetId: 10, firmaId: null, fuarId: 3, userId: 17, isDone: false, notes: "", parentFormId: null,   createdAt: "2026-04-10T08:00:00Z", completedAt: null },
];

function initFilledForms() {
  if (!localStorage.getItem(FILLED_FORMS_KEY)) {
    localStorage.setItem(FILLED_FORMS_KEY, JSON.stringify(SEED_FILLED_FORMS));
  }
}

// ── 7. Form Cevapları key helper ──────────────────────────────────────────────
// API: PUT /api/v1/inspection/form/content  |  GET /form/{formId}/latest-contents
const answersKey = (formId) => `fds_saha_answers_${formId}`;

// ── 8. Service Functions ───────────────────────────────────────────────────────

// ── Backend uyumluluk yardımcısı ─────────────────────────────────────────────
// Backend native SQL query'si Object[] (raw array) döndürebiliyor, obje yerine.
// Beklenen format: { inspection_scope_id, inspection_scope_name, target_entity, ... }
// Backend raw array veya JSON object döndürebilir.
// Array gelirse: kolon sırası belirsiz olabilir (ALTER TABLE pozisyonu değişken).
// Bu yüzden TYPE-DETECTION kullanılır — her koşulda doğru çalışır.
export function normalizeScopeRow(s) {
  if (!Array.isArray(s)) return s;  // Zaten obje → dokunma

  // translated_name JOIN'den gelir → HER ZAMAN dizinin son elemanı (string veya null)
  // Geriye tarama YAPILMAZ — target_entity ("stand") gibi string'ler karışır.
  const lastEl = s[s.length - 1];
  const translatedName = (typeof lastEl === 'string' && lastEl.trim()) ? lastEl : null;

  // inspection_scope_name her zaman non-empty string.
  // inspection_department_id her zaman integer veya null.
  // s[1] string ise → ESKİ format (dept_id yok veya sonda)
  // s[1] integer/null ise → YENİ format (dept_id position 1'de, scope_name position 2'de)
  const deptAtPos1 = typeof s[1] !== 'string';

  if (deptAtPos1) {
    // Yeni format: [scope_id, dept_id, scope_name, description, target_entity, is_deleted, translated_name]
    return {
      inspection_scope_id:          s[0],
      inspection_department_id:     s[1] ?? null,
      inspection_scope_name:        s[2],
      inspection_scope_description: s[3],
      target_entity:                s[4],
      is_deleted:                   s[5],
      translated_name:              translatedName ?? s[2],
    };
  } else {
    // Eski format: [scope_id, scope_name, description, target_entity, is_deleted, translated_name]
    // veya sonda dept_id: [scope_id, scope_name, desc, target, is_del, dept_id, translated_name]
    return {
      inspection_scope_id:          s[0],
      inspection_scope_name:        s[1],
      inspection_scope_description: s[2],
      target_entity:                s[3],
      is_deleted:                   s[4],
      inspection_department_id:     s.length >= 7 ? (s[5] ?? null) : null,
      translated_name:              translatedName ?? s[1],
    };
  }
}

// GET /api/v1/inspection/departments?languageId=1
export async function getDepartments({ languageId = 1 } = {}) {
  if (USE_MOCK_DATA) return [];
  try {
    const res = await axios.get(
        `${INSPECTION_API}/departments?languageId=${languageId}`,
        { headers: getAuthHeader() }
    );
    return (res.data || []).map((item) => normalizeItem('departments', item));
  } catch (err) {
    console.error('[sahaInspectionService] getDepartments:', err);
    return [];
  }
}

// GET /api/v1/inspection/scopes?languageId=1  (&parentId={departmentId} ile departman filtresi)
export async function getScopes({ departmentId, languageId = 1 } = {}) {
  if (USE_MOCK_DATA) return MOCK_SCOPES;
  try {
    const q = new URLSearchParams({ languageId });
    if (departmentId != null) q.set('parentId', String(departmentId));
    const res = await axios.get(
        `${INSPECTION_API}/scopes?${q}`,
        { headers: getAuthHeader() }
    );
    return (res.data || []).map(normalizeScopeRow);
  } catch (err) {
    console.error('[sahaInspectionService] getScopes:', err);
    return [];
  }
}

// GET /api/v1/inspection/all-hazards?languageId=1[&noParentOnly=true]
export async function getAllHazards({ languageId = 1, noParentOnly = false } = {}) {
  if (USE_MOCK_DATA) return [];
  const q = new URLSearchParams({ languageId });
  if (noParentOnly) q.set('noParentOnly', 'true');
  try {
    const res = await axios.get(
        `${INSPECTION_API}/all-hazards?${q}`,
        { headers: getAuthHeader() }
    );
    return (res.data || []).map((item) => normalizeItem('hazards', item));
  } catch (err) {
    console.error('[sahaInspectionService] getAllHazards:', err);
    return [];
  }
}

// GET /api/v1/inspection/hazard-tree?hazardId=X&languageId=1
// Session 12 güncelleme: Response artık suggestions[] + legalProcedures[] içeriyor (additive).
// Shape: { hazardId, hazardName, risks: [{ riskId, riskName, nonconformities: [...] }],
//          suggestions: [{ inspection_suggestion_id, inspection_suggestion_name, ... }],
//          legalProcedures: [{ inspection_legal_procedure_id, inspection_legal_procedure_name, ... }] }
// Tek çağrıda hazard + riskleri + öneriler + mevzuatlar gelir — ayrı junction çağrısı gerekmez.
// 404 → { result: false, message: "Hazard not found with ID: X", data: null }
export async function getHazardTree(hazardId, { languageId = 1 } = {}) {
  if (USE_MOCK_DATA) return null;
  try {
    const res = await axios.get(
        `${INSPECTION_API}/hazard-tree?hazardId=${hazardId}&languageId=${languageId}`,
        { headers: getAuthHeader() }
    );
    return res.data || null;
  } catch (err) {
    console.error('[sahaInspectionService] getHazardTree:', err);
    return null;
  }
}

// GET /api/v1/inspection/hazards/{hazardId}/nonconformities?languageId=1
// Bölüm 25-F — Nonconformity Sidebar: Hazard altındaki tüm riskleri getirir
// Kullanım: NC Modal'da hazard seçilince geçmiş kayıtları yan panel olarak göster
// Endpoint + raw-array normalizasyonu inspectionAdminService'te tek kaynakta
// duruyor (aynı /inspection base, aynı sütun sırası). Burada sadece saha
// konvansiyonu sarmalanıyor: hata yutulur ve [] dönülür, çünkü çağıran
// HazardNcSuggester try/catch'siz await ediyor — throw spinner'ı kilitler.
export async function getHazardNonconformities(hazardId, { languageId = 1 } = {}) {
  if (USE_MOCK_DATA) return [];
  if (!hazardId) return [];
  try {
    return await adminGetHazardNonconformities(hazardId, { languageId });
  } catch (err) {
    console.error('[sahaInspectionService] getHazardNonconformities:', err);
    return [];
  }
}

// GET /api/v1/inspection/types?parentId={scopeId}&languageId=1
// Backend raw array döndürebilir: [id, scopeId, name, shortName, mainQuestion, ...]
// normalizeItem('types') her öğeyi objeye çevirir → inspection_type_id erişimi sağlanır
export async function getTypes(scopeId) {
  if (USE_MOCK_DATA) return MOCK_TYPES[scopeId] || [];
  try {
    const res = await axios.get(
        `${INSPECTION_API}/types?parentId=${scopeId}&languageId=1`,
        { headers: getAuthHeader() }
    );
    return (res.data || []).map((item) => normalizeItem('types', item));
  } catch (err) {
    console.error('[sahaInspectionService] getTypes:', err);
    return [];
  }
}

// GET /api/v1/inspection/generate?typeId={typeId}&languageId=1
export async function getFormTree(typeId) {
  if (USE_MOCK_DATA) return MOCK_FORM_TREES[typeId] || null;
  try {
    const res = await axios.get(
        `${INSPECTION_API}/generate?typeId=${typeId}&languageId=1`,
        { headers: getAuthHeader() }
    );
    return res.data || null;
  } catch (err) {
    console.error('[sahaInspectionService] getFormTree:', err);
    return null;
  }
}

// GET /api/v1/inspection/nonconformity-tree?questionId={questionId}&languageId=1
export async function getNcTree(questionId) {
  if (USE_MOCK_DATA) return MOCK_NC_TREES[questionId] || DEFAULT_NC_TREE(questionId, `Soru #${questionId}`);
  try {
    const res = await axios.get(
        `${INSPECTION_API}/nonconformity-tree?questionId=${questionId}&languageId=1`,
        { headers: getAuthHeader() }
    );
    return res.data || null;
  } catch (err) {
    console.error('[sahaInspectionService] getNcTree:', err);
    return DEFAULT_NC_TREE(questionId, `Soru #${questionId}`);
  }
}

// targetEntity bazlı picker endpoint'leri
// stand      → GET /api/v1/standTalep/list?fuarId=   (yeni, dokümante edilmiş endpoint)
// salon      → GET /api/v1/fuaralani/salons?fuarId=
// fuar_alani → GET /api/v1/fuaralani/list
// fuar       → aktif fuar ID'si (localStorage'dan)
export async function getTargets(targetEntity, fuarIdOverride) {
  if (USE_MOCK_DATA) return MOCK_TARGETS[targetEntity] || [];
  // fuarIdOverride: sayfa kendi fuarını biliyorsa (Denetim Kayıtları); yoksa localStorage'daki aktif fuar
  const fuarId  = fuarIdOverride || localStorage.getItem('activeFairId') || 3;
  const token   = localStorage.getItem('token')  || '';
  const userId  = localStorage.getItem('userId') || '';
  const headers = {
    ...getAuthHeader(),
    'X-token':   token,   // session token — StandTalep auth için zorunlu
    'X-user-id': userId,  // kullanıcı kimliği
  };
  try {
    switch (targetEntity) {
      case 'stand': {
        // GET /api/v1/standTalep/list?fuarId={id}&page=0&size=500
        // Response: { data: [...], totalCount, page, size }
        const res = await axios.get(
            `${JAVA_BACKEND_API_URL}/standTalep/list?fuarId=${fuarId}&page=0&size=500`,
            { headers }
        );
        return (res.data?.data || []).map((s) => ({
          id:      s.standId,
          name:    s.standName,
          firma:   s.katilimciFirmaName || null,
          salon:   s.salonName          || null,
          firmaId: s.katilimciFirmaId   || null,
        }));
      }
      case 'salon': {
        // GET /api/v1/fuar/{fuarId} → fuarSalons[]  (auth gerektirmez, CORS sorunu yok)
        // /fuaralani/salons?fuarId= CORS hatası verdiği için bu endpoint kullanılıyor
        const res = await axios.get(
            `${JAVA_BACKEND_API_URL}/fuar/${fuarId}`,
            { headers }
        );
        return (res.data?.fuarSalons || []).map((s) => ({
          id: s.id, name: s.salon_name || s.salonName || i18n.t("services:sahaInspectionService.salon", { id: s.id }), firma: null, salon: null,
        }));
      }
      case 'fuar_alani': {
        // GET /api/v1/fuar/listFuarAlani  (FuarController — CORS tutarlı)
        const res = await axios.get(
            `${JAVA_BACKEND_API_URL}/fuar/listFuarAlani`,
            { headers }
        );
        return (res.data || []).map((a) => ({
          id: a.id, name: a.fuarAlaniName, firma: null, salon: null,
        }));
      }
      case 'fuar':
        return [{ id: Number(fuarId), name: i18n.t("services:sahaInspectionService.aktifFuar", { fuarId }), firma: null, salon: null }];
      default:
        return [];
    }
  } catch (err) {
    console.error('[sahaInspectionService] getTargets:', err);
    return [];
  }
}

// GET /api/v1/inspection/forms?page=&limit=&fuarId=&inspectionLevelId=&scopeTargetId=
// scopeTargetId backlog #71: bir stand'a ait denetim formlarını çekmek için eklendi.
// TEK BAŞINA hedef tipini ayırt etmez (bir stand_id bir salon_id ile aynı sayı olabilir,
// bkz. InspectionFormMongoEntity'nin kendi doc yorumu / backend #72(1)) — çağıran, dönen
// satırları inspectionScopeId'nin target_entity'sine göre kendisi filtrelemeli
// (bkz. FormInstancesPage.jsx'teki scopeMap/isStand deseni).
export async function getAllFilledForms(params = {}) {
  initFilledForms();
  if (USE_MOCK_DATA) {
    return JSON.parse(localStorage.getItem(FILLED_FORMS_KEY) || '[]');
  }
  const { fuarId, inspectionLevelId, scopeTargetId, page = 1, limit = 50 } = params;
  const q = new URLSearchParams({ page, limit });
  if (fuarId)            q.set('fuarId', fuarId);
  if (inspectionLevelId) q.set('inspectionLevelId', inspectionLevelId);
  if (scopeTargetId != null) q.set('scopeTargetId', scopeTargetId);
  try {
    const res = await axios.get(
        `${INSPECTION_API}/forms?${q}`,
        { headers: getAuthHeader() }
    );
    return res.data?.records || res.data || [];
  } catch (err) {
    console.error('[sahaInspectionService] getAllFilledForms:', err);
    return [];
  }
}

// Every page of GET /forms for the given filters. The Uygunsuzluk / DÖF lists count and filter on the client, so they
// need all the rows; getAllFilledForms alone stops at its first page (50 by default).
export async function getAllFilledFormsPaged(params = {}, { pageSize = 200, maxPages = 25 } = {}) {
  if (USE_MOCK_DATA) return getAllFilledForms(params);
  const all = [];
  for (let page = 1; page <= maxPages; page++) {
    const batch = await getAllFilledForms({ ...params, page, limit: pageSize });
    all.push(...batch);
    if (batch.length < pageSize) break;
  }
  return all;
}

export async function getFilledFormsByLevel(level) {
  const forms = await getAllFilledForms();
  return forms.filter((f) => f.inspectionLevelId === level);
}

// POST /api/v1/inspection/form/checkOrSave
export async function saveFilledForm(formData) {
  initFilledForms();
  if (USE_MOCK_DATA) {
    const forms = JSON.parse(localStorage.getItem(FILLED_FORMS_KEY) || '[]');
    const existing = forms.find(
        (f) =>
            f.scopeTargetId     === formData.scopeTargetId &&
            f.inspectionLevelId === formData.inspectionLevelId &&
            f.inspectionTypeId  === formData.inspectionTypeId &&
            f.fuarId            === formData.fuarId
    );
    if (existing) return existing;
    const newForm = {
      ...formData,
      id: 'saha' + Date.now(),
      isDone: false,
      createdAt: new Date().toISOString(),
      completedAt: null,
    };
    forms.unshift(newForm);
    localStorage.setItem(FILLED_FORMS_KEY, JSON.stringify(forms));
    return newForm;
  }
  try {
    const res = await axios.post(
        `${INSPECTION_API}/form/checkOrSave`,
        formData,
        { headers: getAuthHeader() }
    );
    return res.data;
  } catch (err) {
    console.error('[sahaInspectionService] saveFilledForm:', err);
    throw err; // form başlatılamıyorsa üst katmana ilet
  }
}

// POST /api/v1/inspection/form/{id}/partial
export async function updateFilledForm(id, partial) {
  if (USE_MOCK_DATA) {
    const forms = JSON.parse(localStorage.getItem(FILLED_FORMS_KEY) || '[]');
    const idx = forms.findIndex((f) => f.id === id);
    if (idx === -1) return null;
    forms[idx] = { ...forms[idx], ...partial };
    localStorage.setItem(FILLED_FORMS_KEY, JSON.stringify(forms));
    return forms[idx];
  }
  try {
    const res = await axios.post(
        `${INSPECTION_API}/form/${id}/partial`,
        partial,
        { headers: getAuthHeader() }
    );
    return res.data;
  } catch (err) {
    console.error('[sahaInspectionService] updateFilledForm:', err);
    throw err;
  }
}

// GET /api/v1/inspection/form/{formId}/latest-contents
// API yanıtı: array → { [questionId]: { value, categoryId, hazardId, riskId, nonconformityId } }
export async function getAnswers(formId) {
  if (USE_MOCK_DATA) {
    return JSON.parse(localStorage.getItem(answersKey(formId)) || '{}');
  }
  try {
    const res = await axios.get(
        `${INSPECTION_API}/form/${formId}/latest-contents`,
        { headers: getAuthHeader() }
    );
    return Object.fromEntries(
        (res.data || []).map((c) => [
          c.questionId,
          {
            value:           c.answerValue,
            categoryId:      c.categoryId,
            hazardId:        c.hazardId        || null,
            riskId:          c.riskId          || null,
            nonconformityId: c.nonconformityId || null,
          },
        ])
    );
  } catch (err) {
    console.error('[sahaInspectionService] getAnswers:', err);
    return {};
  }
}

// PUT /api/v1/inspection/form/content  (upsert)
// strict: true → a failed write throws instead of resolving undefined (the one-shot Uygunsuzluk Oluştur save needs to know).
export async function saveAnswer(formId, questionId, answer, levelId = 1, { strict = false } = {}) {
  if (USE_MOCK_DATA) {
    const current = JSON.parse(localStorage.getItem(answersKey(formId)) || '{}');
    current[questionId] = { ...current[questionId], ...answer, savedAt: new Date().toISOString() };
    localStorage.setItem(answersKey(formId), JSON.stringify(current));
    return;
  }
  try {
    const res = await axios.put(
        `${INSPECTION_API}/form/content`,
        {
          inspectionFormId:  formId,
          categoryId:        answer.categoryId       || null,
          questionId,
          answerValue:       answer.value,
          inspectionLevelId: levelId,
          hazardId:          answer.hazardId         || null,
          riskId:            answer.riskId           || null,
          nonconformityId:   answer.nonconformityId  || null,
        },
        { headers: getAuthHeader() }
    );
    return res.data; // inspectionFormContentId için content _id dönüyor
  } catch (err) {
    console.error('[sahaInspectionService] saveAnswer:', err);
    if (strict) throw err;
    // hata olursa undefined döner — caller null check yapmalı
  }
}

// ── Yeni endpoint'ler ──────────────────────────────────────────────────────────

// GET /api/v1/inspection/levels
export async function getLevels() {
  const MOCK_LEVELS = [
    { inspection_level_id: 1, inspection_level_name: "Denetim Raporu" },
    { inspection_level_id: 2, inspection_level_name: "Uygunsuzluk Formu" },
    { inspection_level_id: 3, inspection_level_name: "Kapalı DÖF (DÖF Formu)" },
  ];
  if (USE_MOCK_DATA) return MOCK_LEVELS;
  try {
    const res = await axios.get(`${INSPECTION_API}/levels`, { headers: getAuthHeader() });
    return res.data || [];
  } catch (err) {
    console.error('[sahaInspectionService] getLevels:', err);
    return [];
  }
}

// POST /api/v1/inspection/form  (doğrudan oluştur — checkOrSave yapmaz)
export async function createForm(formData) {
  if (USE_MOCK_DATA) return saveFilledForm(formData);
  try {
    const res = await axios.post(
        `${INSPECTION_API}/form`,
        formData,
        { headers: getAuthHeader() }
    );
    return res.data;
  } catch (err) {
    console.error('[sahaInspectionService] createForm:', err);
    throw err;
  }
}

// GET /api/v1/inspection/form/{id}
export async function getForm(id) {
  initFilledForms();
  if (USE_MOCK_DATA) {
    const forms = JSON.parse(localStorage.getItem(FILLED_FORMS_KEY) || '[]');
    return forms.find((f) => f.id === id) || null;
  }
  try {
    const res = await axios.get(
        `${INSPECTION_API}/form/${id}`,
        { headers: getAuthHeader() }
    );
    return res.data;
  } catch (err) {
    console.error('[sahaInspectionService] getForm:', err);
    return null;
  }
}

// PUT /api/v1/inspection/form/{id}  (tam güncelleme)
export async function fullUpdateForm(id, formData) {
  if (USE_MOCK_DATA) return updateFilledForm(id, formData);
  try {
    const res = await axios.put(
        `${INSPECTION_API}/form/${id}`,
        formData,
        { headers: getAuthHeader() }
    );
    return res.data;
  } catch (err) {
    console.error('[sahaInspectionService] fullUpdateForm:', err);
    throw err;
  }
}

// GET /api/v1/inspection/form/{id}/children  (Level 2/3 alt formları)
// Session 12 güncelleme: Response artık 5 yeni source* alanı içeriyor (additive):
//   sourceCategoryId      — tetikleyen sorunun kategorisi
//   sourceQuestionId      — "Uygun Değil" işaretlenen soru ID'si
//   sourceHazardId        — content satırındaki hazard (Uygunsuzluk) ID'si
//   sourceRiskId          — content satırındaki risk (Tehlike) ID'si
//   sourceNonconformityId — content satırındaki nonconformity (Risk) ID'si
// Herhangi biri null olabilir — kullanmadan önce null check yapılmalı.
export async function getChildForms(id) {
  initFilledForms();
  if (USE_MOCK_DATA) {
    const forms = JSON.parse(localStorage.getItem(FILLED_FORMS_KEY) || '[]');
    return forms.filter((f) => f.parentFormId === id);
  }
  try {
    const res = await axios.get(
        `${INSPECTION_API}/form/${id}/children`,
        { headers: getAuthHeader() }
    );
    return res.data || [];
  } catch (err) {
    console.error('[sahaInspectionService] getChildForms:', err);
    return [];
  }
}

// GET /api/v1/inspection/form/{formId}/contents  (tüm cevap geçmişi)
export async function getAllContents(formId) {
  if (USE_MOCK_DATA) {
    const raw = JSON.parse(localStorage.getItem(answersKey(formId)) || '{}');
    return Object.entries(raw).map(([qId, ans]) => ({ questionId: Number(qId), ...ans }));
  }
  try {
    const res = await axios.get(
        `${INSPECTION_API}/form/${formId}/contents`,
        { headers: getAuthHeader() }
    );
    return res.data || [];
  } catch (err) {
    console.error('[sahaInspectionService] getAllContents:', err);
    return [];
  }
}

// POST /api/v1/inspection/form/{formId}/photo  (multipart/form-data)
// meta: { fuarId, questionId?, inspectionLevelId?, inspectionFormContentId? }
export async function uploadPhoto(formId, file, meta = {}) {
  if (USE_MOCK_DATA) {
    return {
      id: 'photo_' + Date.now(),
      inspectionFormId: formId,
      questionId: meta.questionId || null,
      inspectionLevelId: meta.inspectionLevelId || 1,
      originalFileName: file.name,
      contentType: file.type,
      createdAt: new Date().toISOString(),
      _mockUrl: URL.createObjectURL(file),
    };
  }
  const fd = new FormData();
  fd.append('file', file);
  if (meta.fuarId != null)                  fd.append('fuarId', String(meta.fuarId));
  if (meta.questionId != null)              fd.append('questionId', String(meta.questionId));
  if (meta.inspectionLevelId != null)       fd.append('inspectionLevelId', String(meta.inspectionLevelId));
  if (meta.inspectionFormContentId != null) fd.append('inspectionFormContentId', meta.inspectionFormContentId);
  try {
    const res = await axios.post(
        `${INSPECTION_API}/form/${formId}/photo`,
        fd,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data',
          },
        }
    );
    return res.data;
  } catch (err) {
    console.error('[sahaInspectionService] uploadPhoto:', err);
    throw err;
  }
}

// Fotoğraf görüntüleme URL'i oluşturur
// Backend: /public/files/{storageCategory}/{storedFileName}  (auth gerektirmez)
export function getPhotoUrl(doc) {
  if (!doc) return null;
  if (doc._mockUrl) return doc._mockUrl;                    // mock mod
  if (doc.url) return doc.url;                              // backend url döndürürse
  if (doc.storageCategory && doc.storedFileName) {
    const base = JAVA_BACKEND_API_URL.replace('/api/v1', '');
    return `${base}/public/files/${doc.storageCategory}/${doc.storedFileName}`;
  }
  return null;
}

// GET /api/v1/inspection/form/{formId}/photos?questionId=&inspectionLevelId=
export async function getPhotos(formId, params = {}) {
  if (USE_MOCK_DATA) return [];
  const q = new URLSearchParams();
  if (params.questionId != null)        q.set('questionId', params.questionId);
  if (params.inspectionLevelId != null) q.set('inspectionLevelId', params.inspectionLevelId);
  const qs = q.toString() ? `?${q}` : '';
  try {
    const res = await axios.get(
        `${INSPECTION_API}/form/${formId}/photos${qs}`,
        { headers: getAuthHeader() }
    );
    return res.data || [];
  } catch (err) {
    console.error('[sahaInspectionService] getPhotos:', err);
    return [];
  }
}

// GET /api/v1/inspection/form/{id}/detail?languageId=1
// Enriched form detayı: tip/kapsam/hedef/firma/kullanıcı isimleri + içerik satırlarında isimler
export async function getFormDetail(formId, languageId = 1) {
  if (USE_MOCK_DATA) return null;
  try {
    const res = await axios.get(
        `${INSPECTION_API}/form/${formId}/detail?languageId=${languageId}`,
        { headers: getAuthHeader() }
    );
    return res.data || null;
  } catch (err) {
    console.error('[sahaInspectionService] getFormDetail:', err);
    return null;
  }
}

// ── Uygunsuzluk Oluştur (#603 / backend #604) ─────────────────────────────────

// GET /api/v1/inspection/hazard-paths  — her canlı Uygunsuzluk (hazard) + Birim › Kapsam › Form Türü › Kategori › Soru yolu.
// Katalog küçük (yüzlerce satır): tamamı bir kez çekilir, arama istemcide yapılır. languageId gönderilmez →
// isimler isteğin Accept-Language başlığındaki dilde gelir (eksik çeviri → Türkçe).
// Yolu eksik satırlar (Soru / Form Türü / Kapsam yok) bu akışta kaydedilemez, atılır.
export async function getHazardPaths() {
  if (USE_MOCK_DATA) return [];
  const res = await axios.get(`${INSPECTION_API}/hazard-paths`, { headers: getAuthHeader() });
  return (res.data || [])
    .map((r) => ({
      hazardId:     r.hazard_id,
      hazardName:   (r.hazard_name   || '').trim(), // bazı kayıtlar başında sekme karakteriyle geliyor
      questionId:   r.question_id,
      questionName: (r.question_name || '').trim(),
      categoryId:   r.category_id,
      categoryName: (r.category_name || '').trim(),
      typeId:       r.type_id,
      typeName:     (r.type_name     || '').trim(),
      scopeId:      r.scope_id,
      scopeName:    (r.scope_name    || '').trim(),
      targetEntity: r.target_entity,
      departmentId: r.department_id ?? null,
      departmentName: (r.department_name || '').trim(),
    }))
    .filter((r) => r.hazardId != null && r.questionId != null && r.typeId != null && r.scopeId != null);
}

// POST /api/v1/inspection/form  { isDraft: true }  — foto-önce taslağı; userId'yi backend oturumdan alır
export async function createDraftForm({ fuarId, userId }) {
  const res = await axios.post(
      `${INSPECTION_API}/form`,
      { inspectionLevelId: 2, fuarId, userId, isDraft: true },
      { headers: getAuthHeader() }
  );
  return res.data;
}

// POST /api/v1/inspection/form/{id}/discard  — kendi taslağını bırakır (bayrak, silme değil)
export async function discardDraftForm(formId) {
  const res = await axios.post(
      `${INSPECTION_API}/form/${formId}/discard`,
      null,
      { headers: getAuthHeader() }
  );
  return res.data;
}

// GET /api/v1/inspection/forms?myDrafts=true  — çağıranın açık Uygunsuzluk taslakları
export async function getMyDrafts({ fuarId } = {}) {
  const q = new URLSearchParams({ page: 1, limit: 50, inspectionLevelId: 2, myDrafts: true });
  if (fuarId) q.set('fuarId', fuarId);
  try {
    const res = await axios.get(`${INSPECTION_API}/forms?${q}`, { headers: getAuthHeader() });
    return res.data?.records || res.data || [];
  } catch (err) {
    console.error('[sahaInspectionService] getMyDrafts:', err);
    return [];
  }
}

// ── Enrichment lookups ────────────────────────────────────────────────────────
// Mock verisiyle başlar (her iki modda da hemen kullanılabilir).
// API modunda populateEnrichmentCache() gerçek veriyle üzerine yazar.
export const TYPE_NAMES   = Object.fromEntries(
    Object.values(MOCK_TYPES).flat().map((t) => [t.inspection_type_id, t.inspection_type_name])
);
export const SCOPE_NAMES  = Object.fromEntries(
    MOCK_SCOPES.map((s) => [s.inspection_scope_id, s.inspection_scope_name])
);
export const TARGET_NAMES = Object.fromEntries(
    Object.values(MOCK_TARGETS).flat().map((t) => [t.id, t.name])
);
export const FIRMA_NAMES = {
  1: 'Akdeniz A.Ş.', 2: 'Karadeniz Ltd.', 3: 'Ege Tic.',
  4: 'Marmara San.', 5: 'Tüm Stand A.Ş.', null: '—',
};

// API modunda scopes + types + targets çekip cache'i günceller.
// Bileşenler mount'ta bir kez çağırabilir; birden fazla çağrılsa da zarar vermez.
export async function populateEnrichmentCache() {
  if (USE_MOCK_DATA) return; // mock modda güncelleme gerekmez
  try {
    // Scope adları
    const scopes = await getScopes();
    scopes.forEach((s) => {
      SCOPE_NAMES[s.inspection_scope_id] =
          s.translated_name || s.inspection_scope_name || '';
    });

    // Type adları (her scope için)
    const typeArrays = await Promise.all(
        scopes.map((s) => getTypes(s.inspection_scope_id).catch(() => []))
    );
    typeArrays.flat().forEach((t) => {
      TYPE_NAMES[t.inspection_type_id] =
          t.translated_name || t.inspection_type_name || '';
    });

    // Target adları (stand / salon / fuar_alani / fuar)
    const entityTypes = ['stand', 'salon', 'fuar_alani', 'fuar'];
    const targetArrays = await Promise.all(
        entityTypes.map((e) => getTargets(e).catch(() => []))
    );
    targetArrays.flat().forEach((t) => {
      TARGET_NAMES[t.id] = t.name;
    });
  } catch (err) {
    console.error('[sahaInspectionService] populateEnrichmentCache:', err);
  }
}
